import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import {
  clearCompletedPaymentIntentKeys,
  settlePaymentIntentKeyForTransaction,
  shouldRotatePaymentIntentKey,
} from "@/lib/paymentSafety";

export type VerifyStatus =
  | "idle"
  | "verifying"
  | "paid_and_activated"
  | "paid_activation_pending"
  | "paid_activation_failed"
  | "paid_invoice_pending"
  | "failed"
  | "cancelled"
  | "expired"
  | "abandoned"
  | "refunded"
  | "pending"
  | "error";

export interface VerifyPaymentResult {
  status: VerifyStatus;
  transaction_id?: string;
  reason?: string;
  error_code?: string;
  retryable?: boolean;
  correlation_id?: string;
}

type PortalClient = SupabaseClient<Database>;

export function useVerifyPayment(
  opts: { enabled?: boolean; maxAttempts?: number; client?: PortalClient } = {},
) {
  const { enabled = true, maxAttempts = 5, client = supabase } = opts;
  const [result, setResult] = useState<VerifyPaymentResult>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;

    const params = new URLSearchParams(window.location.search);
    const gatewayPaymentId = params.get("id");
    const transactionId = params.get("transaction_id");
    if (!gatewayPaymentId) return;

    let cancelled = false;
    let attempts = 0;

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      ["id", "transaction_id", "status", "message", "amount", "fee", "currency"].forEach((key) =>
        url.searchParams.delete(key)
      );
      window.history.replaceState({}, "", url.toString());
    };

    const verify = async () => {
      attempts += 1;
      setResult({ status: "verifying" });

      try {
        const { data, error } = await client.functions.invoke("verify-payment", {
          body: {
            transaction_id: transactionId || undefined,
            gateway_payment_id: gatewayPaymentId || undefined,
          },
        });

        if (cancelled) return;
        if (error) {
          setResult({ status: "error", reason: error.message });
          cleanUrl();
          return;
        }

        const status = data?.status as VerifyStatus | undefined;
        if (status === "paid_and_activated" || status === "paid_activation_failed" || status === "paid_invoice_pending" || status === "failed" || status === "cancelled" || status === "expired" || status === "abandoned" || status === "refunded") {
          if (status !== "paid_activation_failed" && shouldRotatePaymentIntentKey(status)) {
            const cleared = settlePaymentIntentKeyForTransaction(
              data?.transaction_id ?? transactionId,
              status,
            );
            if (!cleared) clearCompletedPaymentIntentKeys();
          }
          setResult({
            status,
            transaction_id: data?.transaction_id,
            reason: data?.reason,
            error_code: data?.error_code,
            retryable: data?.retryable,
            correlation_id: data?.correlation_id,
          });
          cleanUrl();
          return;
        }

        if ((status === "pending" || status === "paid_activation_pending") && attempts < maxAttempts) {
          setTimeout(verify, 2000 + attempts * 1000);
          return;
        }

        setResult({
          status: status === "paid_activation_pending" ? "paid_activation_pending" : "pending",
          transaction_id: data?.transaction_id,
          reason: data?.reason,
        });
        cleanUrl();
      } catch (error) {
        if (cancelled) return;
        setResult({
          status: "error",
          reason: error instanceof Error ? error.message : "verify_failed",
        });
        cleanUrl();
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [client, enabled, maxAttempts]);

  return result;
}
